/**
 * Remembers where to send someone after they sign in, so a shared report link
 * still opens once the HR has logged in.
 *
 * Stored in localStorage because the SSO sign-in leaves the app and comes back
 * on a fresh page load. Entries expire so an abandoned sign-in does not hijack
 * an unrelated login days later.
 */

/** Roles allowed to open a shared assessment report. */
export const REPORT_VIEWER_ROLES: string[] = ['FRACTAL_HR','CLIENT_HR'];

export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

const MAX_AGE_MS = 30 * 60 * 1000;

// Only in-app report links are ever replayed; anything else is dropped.
const ALLOWED_PREFIX = '/assessment/public';

export function savePostLoginRedirect(url: string): void {
  if (!url?.startsWith(ALLOWED_PREFIX)) {
    return;
  }
  try {
    localStorage.setItem(
      POST_LOGIN_REDIRECT_KEY,
      JSON.stringify({ url, savedAt: Date.now() })
    );
  } catch {
    // Storage blocked: the HR lands on the dashboard instead, which is today's behaviour.
  }
}

/** Returns the pending link (if still valid) and removes it either way. */
export function consumePostLoginRedirect(): string | null {
  try {
    const raw = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    if (!raw) {
      return null;
    }
    const { url, savedAt } = JSON.parse(raw);
    const fresh = typeof savedAt === 'number' && Date.now() - savedAt < MAX_AGE_MS;
    return fresh && typeof url === 'string' && url.startsWith(ALLOWED_PREFIX)
      ? url
      : null;
  } catch {
    return null;
  }
}
